import { createAsyncThunk } from '@reduxjs/toolkit';

import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';

import { ConflitoFluxoError, fluxosArbitralService } from '../services/fluxosArbitralService';
import type { ProblemaValidacao } from '../services/fluxoValidacoes';
import type { CapabilitiesMotor, VersaoFluxo } from '../types';
import type { FluxoArbitralEditavel, MotivoSomenteEditorDeNos } from '../types/editor';

export const fetchFluxoArbitral = createAsyncThunk<
  FluxoArbitralEditavel,
  string,
  { rejectValue: string }
>('fluxoEditor/fetch', async (fluxoId, { rejectWithValue }) => {
  try {
    const fluxo = await fluxosArbitralService.obter(fluxoId);
    registrarSomenteEditorDeNos(fluxo);
    return fluxo;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao carregar fluxo';
    return rejectWithValue(message);
  }
});

/**
 * Quantos fluxos a régua recusa salvar, e por quê: é o número que diz se a guarda acerta ou se
 * trava fluxo que a régua salvaria igual (falso positivo). Só ids e contagens.
 */
function registrarSomenteEditorDeNos(fluxo: FluxoArbitralEditavel): void {
  const motivo = fluxo.motivoSomenteEditorDeNos;
  if (!motivo) return;
  registrarAcao('fluxoEditor.somenteEditorDeNos', {
    fluxoId: fluxo.id,
    motivo: motivo.tipo,
    ...detalheDoMotivo(motivo),
  });
}

/**
 * Os parâmetros saem só com o nome da chave do `node.data` (`visivelPara`, `mode`): é o que diz
 * qual campo a volta ainda não cobre, sem levar o valor (texto de andamento, título de pendência).
 */
function detalheDoMotivo(motivo: MotivoSomenteEditorDeNos): Record<string, unknown> {
  switch (motivo.tipo) {
    case 'APAGARIA_BLOCOS':
      return { blocosApagados: motivo.quantidade };
    case 'MUDARIA_CAMINHO':
      return {
        ligacoesSoNoSalvo: motivo.ligacoesSoNoSalvo.length,
        ligacoesSoNaRegua: motivo.ligacoesSoNaRegua.length,
        inicioMuda: motivo.inicioMuda,
      };
    case 'TROCARIA_PARAMETROS':
      return {
        parametrosTrocados: motivo.parametros.length,
        chavesTrocadas: [...new Set(motivo.parametros.map((parametro) => parametro.chave))]
          .sort()
          .join(','),
      };
  }
}

export interface SalvarFluxoRejeicao {
  tipo: 'erro' | 'conflito';
  message: string;
  versionAtual?: number | null;
}

export const salvarFluxoArbitral = createAsyncThunk<
  FluxoArbitralEditavel,
  FluxoArbitralEditavel,
  { rejectValue: SalvarFluxoRejeicao }
>(
  'fluxoEditor/salvar',
  async (fluxo, { rejectWithValue }) => {
    try {
      return await fluxosArbitralService.salvarDefinition(fluxo);
    } catch (error) {
      if (error instanceof ConflitoFluxoError) {
        return rejectWithValue({
          tipo: 'conflito',
          message: 'Este fluxo foi alterado por outra sessão.',
          versionAtual: error.versionAtual,
        });
      }
      const message = error instanceof Error ? error.message : 'Erro ao salvar fluxo';
      return rejectWithValue({ tipo: 'erro', message });
    }
  },
  { condition: (fluxo) => fluxo.motivoSomenteEditorDeNos === null },
);

export const validarFluxoArbitral = createAsyncThunk<
  ProblemaValidacao[],
  string,
  { rejectValue: string }
>('fluxoEditor/validar', async (fluxoId, { rejectWithValue }) => {
  try {
    return await fluxosArbitralService.validar(fluxoId);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao validar fluxo';
    return rejectWithValue(message);
  }
});

export const fetchVersoesFluxo = createAsyncThunk<VersaoFluxo[], string, { rejectValue: string }>(
  'fluxoEditor/versoes',
  async (fluxoId, { rejectWithValue }) => {
    try {
      return await fluxosArbitralService.listarVersoes(fluxoId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar versões';
      return rejectWithValue(message);
    }
  },
);

export const fetchCapabilities = createAsyncThunk<CapabilitiesMotor, void, { rejectValue: string }>(
  'fluxoEditor/capabilities',
  async (_, { rejectWithValue }) => {
    try {
      return await fluxosArbitralService.obterCapabilities();
    } catch (error) {
      // Sem capabilities o editor esconde o que depende do motor (ex.: "avisar e continuar
      // aguardando") sem dizer nada na tela; o RUM é onde dá para ver que foi falha, não ausência.
      reportError(error instanceof Error ? error : new Error(String(error)), {
        source: 'fluxoEditor.capabilities',
      });
      const message = error instanceof Error ? error.message : 'Erro ao carregar capacidades';
      return rejectWithValue(message);
    }
  },
);
